import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { 
  Briefcase,
  Plus,
  Edit,
  Eye,
  Trash2,
  Search,
  Filter,
  Users,
  Building,
  TrendingUp,
  ChevronDown,
  CheckCircle,
  AlertTriangle,
  Clock,
  Target,
  FileText,
  Printer,
  Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GerenciarCargos = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cargos, setCargos] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [competencias, setCompetencias] = useState([]);
  const [horariosTrabalho, setHorariosTrabalho] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNivel, setFilterNivel] = useState('');
  const [filterDepartamento, setFilterDepartamento] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [cargoSelecionado, setCargoSelecionado] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [cargoIdEditando, setCargoIdEditando] = useState(null);
  const [sugerindoCbo, setSugerindoCbo] = useState(false);
  
  const [formData, setFormData] = useState({
    nome_cargo: '',
    codigo_cargo: '',
    cbo: '',
    departamento: '',
    nivel_hierarquico: 'junior',
    superior_imediato: '',
    cargo_sensivel: false,
    grau_sensibilidade: '',
    missao_proposito: '',
    atividades: [
      { descricao: '', tipo: 'essencial', percentual_tempo: 70 }
    ],
    competencias_tecnicas: [],
    competencias_comportamentais: [],
    competencias_gerenciais: [],
    competencias_obrigatorias: {
      tecnicas: [],
      comportamentais: [],
      gerenciais: []
    },
    requisitos: [
      { tipo: 'formacao', descricao: '', obrigatorio: true, detalhes: '' }
    ],
    condicoes_trabalho: {
      ambiente: 'escritorio',
      horario: 'comercial',
      viagens: false,
      home_office: false
    }
  });

  const niveisHierarquicos = [
    { value: 'trainee', label: 'Trainee' },
    { value: 'junior', label: 'Júnior' },
    { value: 'pleno', label: 'Pleno' },
    { value: 'senior', label: 'Senior' },
    { value: 'coordenacao', label: 'Coordenação' },
    { value: 'gerencia', label: 'Gerência' },
    { value: 'diretoria', label: 'Diretoria' }
  ];

  const departamentos = [
    'Recursos Humanos', 'Tecnologia', 'Comercial', 'Administrativo', 
    'Financeiro', 'Marketing', 'Operacional', 'Jurídico'
  ];

  useEffect(() => {
    loadData();
  }, []);

  const clearNewParam = () => {
    if (!searchParams.get('new')) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('new');
    setSearchParams(nextParams, { replace: true });
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setCargoIdEditando(null);
    resetForm();
    setShowCreateModal(true);
    clearNewParam();
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setIsEditMode(false);
    setCargoIdEditando(null);
    clearNewParam();
  };

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      openCreateModal();
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      await Promise.all([
        loadCargos(),
        loadFuncionarios(),
        loadCompetencias(),
        loadHorariosTrabalho()
      ]);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCargos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/dcf/cargos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCargos(response.data);
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
      // Mock data
      setCargos([
        {
          id: '1',
          nome_cargo: 'Analista de RH',
          codigo_cargo: 'RH-ANL-001',
          departamento: 'Recursos Humanos',
          nivel_hierarquico: 'pleno',
          status: 'ativo',
          funcionarios_vinculados: 3,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: '2',
          nome_cargo: 'Desenvolvedor Senior',
          codigo_cargo: 'TI-DEV-001',
          departamento: 'Tecnologia',
          nivel_hierarquico: 'senior',
          status: 'ativo',
          funcionarios_vinculados: 5,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ]);
    }
  };

  const loadFuncionarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const funcionariosAtivos = response.data.filter(emp => {
        const situacao = emp.situacao?.toLowerCase();
        const status = emp.status?.toLowerCase();
        return situacao === 'ativo' || status === 'ativo' || emp.ativo === true;
      });
      
      setFuncionarios(funcionariosAtivos);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const loadCompetencias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/dcf/competencias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompetencias(response.data);
    } catch (error) {
      console.error('Erro ao carregar competências:', error);
      // Mock data
      setCompetencias([
        { id: '1', nome: 'Excel Avançado', tipo: 'tecnica' },
        { id: '2', nome: 'PowerBI', tipo: 'tecnica' },
        { id: '3', nome: 'Comunicação', tipo: 'comportamental' },
        { id: '4', nome: 'Liderança', tipo: 'comportamental' },
        { id: '5', nome: 'Gestão de Pessoas', tipo: 'gerencial' }
      ]);
    }
  };

  const loadHorariosTrabalho = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/ponto/horarios-trabalho`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHorariosTrabalho(response.data || []);
      console.log('✅ Horários de trabalho carregados:', response.data?.length || 0);
    } catch (error) {
      console.error('Erro ao carregar horários de trabalho:', error);
      setHorariosTrabalho([]);
    }
  };

  const handleCreateCargo = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (isEditMode && cargoIdEditando) {
        // Modo Edição - atualizar cargo existente
        await axios.put(`${API}/api/dcf/templates/${cargoIdEditando}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('✅ Cargo atualizado com sucesso!');
      } else {
        // Modo Criação - criar novo cargo
        await axios.post(`${API}/api/dcf/cargos`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('✅ Cargo DCF criado com sucesso!');
      }
      
      closeCreateModal();
      resetForm();
      loadCargos();
    } catch (error) {
      console.error('Erro ao salvar cargo:', error);
      alert('❌ Erro ao salvar cargo DCF');
    }
  };

  const resetForm = () => {
    setFormData({
      nome_cargo: '',
      codigo_cargo: '',
      cbo: '',
      departamento: '',
      nivel_hierarquico: 'junior',
      superior_imediato: '',
      cargo_sensivel: false,
      grau_sensibilidade: '',
      missao_proposito: '',
      atividades: [
        { descricao: '', tipo: 'essencial', percentual_tempo: 70 }
      ],
      competencias_tecnicas: [],
      competencias_comportamentais: [],
      competencias_gerenciais: [],
      competencias_obrigatorias: {
        tecnicas: [],
        comportamentais: [],
        gerenciais: []
      },
      requisitos: [
        { tipo: 'formacao', descricao: '', obrigatorio: true, detalhes: '' }
      ],
      condicoes_trabalho: {
        ambiente: 'escritorio',
        horario: 'comercial',
        viagens: false,
        home_office: false
      }
    });
  };


  const sugerirCBO = async () => {
    if (!formData.nome_cargo || !formData.nome_cargo.trim()) {
      alert('❌ Por favor, preencha o nome do cargo primeiro');
      return;
    }
    
    setSugerindoCbo(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/dcf/sugerir-cbo`,
        { cargo_nome: formData.nome_cargo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.success && response.data.cbo) {
        const cboData = response.data.cbo;
        setFormData(prev => ({
          ...prev,
          cbo: cboData.codigo_cbo
        }));
        
        // Mostrar informação sobre o CBO sugerido
        alert(`✅ CBO Sugerido: ${cboData.codigo_cbo}\n\n${cboData.titulo_cbo}\n\n${cboData.justificativa}`);
      }
    } catch (error) {
      console.error('Erro ao sugerir CBO:', error);
      alert('❌ Erro ao sugerir CBO. Você pode digitar manualmente.');
    } finally {
      setSugerindoCbo(false);
    }
  };

  // Gera o HTML do documento DCF profissional
  const generateDCFDocument = (cargo) => {
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR');
    };

    const getNivelLabel = (nivel) => {
      const niveis = {
        'trainee': 'Trainee',
        'junior': 'Júnior',
        'pleno': 'Pleno',
        'senior': 'Sênior',
        'coordenacao': 'Coordenação',
        'gerencia': 'Gerência',
        'diretoria': 'Diretoria'
      };
      return niveis[nivel] || nivel;
    };

    const getGrauLabel = (grau) => {
      const graus = {
        'baixo': '🟢 Baixo',
        'medio': '🟡 Médio',
        'alto': '🟠 Alto',
        'critico': '🔴 Crítico'
      };
      return graus[grau] || grau;
    };

    const getCompetenciaNome = (compId) => {
      const comp = competencias.find(c => c.id === compId);
      return comp ? comp.nome : compId;
    };

    const cargoSuperior = cargo.superior_imediato 
      ? cargos.find(c => c.id === cargo.superior_imediato)?.nome_cargo || 'N/A'
      : 'N/A';

    return `
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>DCF - ${cargo.nome_cargo}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            background: #fff;
          }
          .container { max-width: 800px; margin: 0 auto; padding: 20px; }
          
          /* Header */
          .header {
            background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
            color: white;
            padding: 30px;
            border-radius: 10px 10px 0 0;
            margin-bottom: 0;
          }
          .header h1 { font-size: 24px; margin-bottom: 5px; }
          .header .subtitle { font-size: 14px; opacity: 0.9; }
          .header .codigo { 
            display: inline-block;
            background: rgba(255,255,255,0.2);
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 12px;
            margin-top: 10px;
          }
          
          /* Info Bar */
          .info-bar {
            display: flex;
            justify-content: space-between;
            background: #f1f5f9;
            padding: 15px 30px;
            border-bottom: 2px solid #e2e8f0;
          }
          .info-item { text-align: center; }
          .info-item .label { font-size: 11px; color: #64748b; text-transform: uppercase; }
          .info-item .value { font-size: 14px; font-weight: 600; color: #1e293b; }
          
          /* Content */
          .content { padding: 30px; background: #fff; }
          
          /* Section */
          .section {
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          .section-title {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 16px;
            font-weight: 600;
            color: #1e40af;
            margin-bottom: 15px;
            padding-bottom: 8px;
            border-bottom: 2px solid #dbeafe;
          }
          .section-title .icon {
            width: 24px;
            height: 24px;
            background: #dbeafe;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
          }
          
          /* Mission Box */
          .mission-box {
            background: #f0fdf4;
            border-left: 4px solid #22c55e;
            padding: 15px 20px;
            border-radius: 0 8px 8px 0;
          }
          .mission-box p { color: #166534; }
          
          /* Activity List */
          .activity-list { list-style: none; }
          .activity-item {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            padding: 12px;
            background: #f8fafc;
            border-radius: 8px;
            margin-bottom: 10px;
          }
          .activity-number {
            min-width: 28px;
            height: 28px;
            background: #3b82f6;
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 600;
          }
          .activity-content { flex: 1; }
          .activity-desc { font-size: 14px; color: #334155; }
          .activity-meta {
            display: flex;
            gap: 15px;
            margin-top: 5px;
          }
          .activity-badge {
            font-size: 11px;
            padding: 2px 8px;
            border-radius: 12px;
            background: #e2e8f0;
            color: #475569;
          }
          .activity-badge.essential { background: #fef3c7; color: #92400e; }
          .activity-badge.secondary { background: #e0e7ff; color: #3730a3; }
          
          /* Competency Grid */
          .competency-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
          }
          .competency-card {
            background: #f8fafc;
            border-radius: 8px;
            padding: 15px;
          }
          .competency-card h4 {
            font-size: 12px;
            text-transform: uppercase;
            color: #64748b;
            margin-bottom: 10px;
          }
          .competency-tag {
            display: inline-block;
            background: #dbeafe;
            color: #1e40af;
            padding: 4px 10px;
            border-radius: 15px;
            font-size: 12px;
            margin: 3px;
          }
          
          /* Requirements */
          .requirement-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px 15px;
            background: #fafafa;
            border-radius: 6px;
            margin-bottom: 8px;
          }
          .requirement-badge {
            font-size: 11px;
            padding: 2px 8px;
            border-radius: 4px;
          }
          .required { background: #fee2e2; color: #991b1b; }
          .optional { background: #e0e7ff; color: #3730a3; }
          
          /* Conditions Grid */
          .conditions-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
          .condition-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px;
            background: #f8fafc;
            border-radius: 8px;
          }
          .condition-icon {
            font-size: 20px;
          }
          .condition-label { font-size: 12px; color: #64748b; }
          .condition-value { font-size: 14px; font-weight: 500; color: #1e293b; }
          
          /* Sensitive Badge */
          .sensitive-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: #fef3c7;
            color: #92400e;
            padding: 6px 12px;
            border-radius: 6px;
            font-size: 13px;
            font-weight: 500;
            margin-top: 10px;
          }
          
          /* Footer */
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e2e8f0;
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            color: #94a3b8;
          }
          
          @media print {
            body { background: white; }
            .container { max-width: 100%; padding: 0; }
            .header { border-radius: 0; }
            .section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <!-- Header -->
          <div class="header">
            <div class="subtitle">DESCRIÇÃO DE CARGO E FUNÇÃO</div>
            <h1>${cargo.nome_cargo}</h1>
            <span class="codigo">Código: ${cargo.codigo_cargo || 'N/A'} | CBO: ${cargo.cbo || 'N/A'}</span>
            ${cargo.cargo_sensivel ? `<div class="sensitive-badge">⚠️ Cargo Sensível - Grau: ${getGrauLabel(cargo.grau_sensibilidade)}</div>` : ''}
          </div>
          
          <!-- Info Bar -->
          <div class="info-bar">
            <div class="info-item">
              <div class="label">Departamento</div>
              <div class="value">${cargo.departamento}</div>
            </div>
            <div class="info-item">
              <div class="label">Nível Hierárquico</div>
              <div class="value">${getNivelLabel(cargo.nivel_hierarquico)}</div>
            </div>
            <div class="info-item">
              <div class="label">Superior Imediato</div>
              <div class="value">${cargoSuperior}</div>
            </div>
            <div class="info-item">
              <div class="label">Status</div>
              <div class="value">${cargo.status === 'ativo' ? '✅ Ativo' : cargo.status === 'inativo' ? '❌ Inativo' : '⏳ Em Revisão'}</div>
            </div>
          </div>
          
          <!-- Content -->
          <div class="content">
            <!-- Missão -->
            <div class="section">
              <div class="section-title">
                <span class="icon">🎯</span>
                Missão / Propósito do Cargo
              </div>
              <div class="mission-box">
                <p>${cargo.missao_proposito || 'Não definido'}</p>
              </div>
            </div>
            
            <!-- Atividades -->
            <div class="section">
              <div class="section-title">
                <span class="icon">⚡</span>
                Principais Atividades e Responsabilidades
              </div>
              <ul class="activity-list">
                ${(cargo.atividades || []).map((ativ, idx) => `
                  <li class="activity-item">
                    <span class="activity-number">${idx + 1}</span>
                    <div class="activity-content">
                      <div class="activity-desc">${ativ.descricao || 'Atividade não especificada'}</div>
                      <div class="activity-meta">
                        <span class="activity-badge ${ativ.tipo === 'essencial' ? 'essential' : 'secondary'}">
                          ${ativ.tipo === 'essencial' ? '★ Essencial' : '○ Secundária'}
                        </span>
                        <span class="activity-badge">${ativ.percentual_tempo || 0}% do tempo</span>
                      </div>
                    </div>
                  </li>
                `).join('')}
              </ul>
            </div>
            
            <!-- Competências -->
            <div class="section">
              <div class="section-title">
                <span class="icon">💡</span>
                Competências Necessárias
              </div>
              <div class="competency-grid">
                <div class="competency-card">
                  <h4>Técnicas</h4>
                  ${(cargo.competencias_tecnicas || []).length > 0 
                    ? cargo.competencias_tecnicas.map(c => `<span class="competency-tag">${getCompetenciaNome(c)}</span>`).join('')
                    : '<span style="color:#94a3b8;font-size:12px;">Não especificadas</span>'
                  }
                </div>
                <div class="competency-card">
                  <h4>Comportamentais</h4>
                  ${(cargo.competencias_comportamentais || []).length > 0 
                    ? cargo.competencias_comportamentais.map(c => `<span class="competency-tag">${getCompetenciaNome(c)}</span>`).join('')
                    : '<span style="color:#94a3b8;font-size:12px;">Não especificadas</span>'
                  }
                </div>
                <div class="competency-card">
                  <h4>Gerenciais</h4>
                  ${(cargo.competencias_gerenciais || []).length > 0 
                    ? cargo.competencias_gerenciais.map(c => `<span class="competency-tag">${getCompetenciaNome(c)}</span>`).join('')
                    : '<span style="color:#94a3b8;font-size:12px;">Não especificadas</span>'
                  }
                </div>
              </div>
            </div>
            
            <!-- Requisitos -->
            <div class="section">
              <div class="section-title">
                <span class="icon">📋</span>
                Requisitos do Cargo
              </div>
              ${(cargo.requisitos || []).length > 0 
                ? cargo.requisitos.map(req => `
                  <div class="requirement-item">
                    <span class="requirement-badge ${req.obrigatorio ? 'required' : 'optional'}">
                      ${req.obrigatorio ? 'Obrigatório' : 'Desejável'}
                    </span>
                    <span><strong>${req.tipo === 'formacao' ? '🎓 Formação' : req.tipo === 'experiencia' ? '💼 Experiência' : req.tipo === 'certificacao' ? '📜 Certificação' : req.tipo === 'idioma' ? '🌍 Idioma' : '📌 Outro'}:</strong> ${req.descricao} ${req.detalhes ? `(${req.detalhes})` : ''}</span>
                  </div>
                `).join('')
                : '<p style="color:#94a3b8;">Nenhum requisito especificado</p>'
              }
            </div>
            
            <!-- Condições de Trabalho -->
            <div class="section">
              <div class="section-title">
                <span class="icon">🏢</span>
                Condições de Trabalho
              </div>
              <div class="conditions-grid">
                <div class="condition-item">
                  <span class="condition-icon">🏠</span>
                  <div>
                    <div class="condition-label">Ambiente</div>
                    <div class="condition-value">${cargo.condicoes_trabalho?.ambiente === 'escritorio' ? 'Escritório' : cargo.condicoes_trabalho?.ambiente === 'externo' ? 'Externo' : cargo.condicoes_trabalho?.ambiente === 'hibrido' ? 'Híbrido' : 'Não especificado'}</div>
                  </div>
                </div>
                <div class="condition-item">
                  <span class="condition-icon">⏰</span>
                  <div>
                    <div class="condition-label">Horário</div>
                    <div class="condition-value">${cargo.condicoes_trabalho?.horario === 'comercial' ? 'Comercial' : cargo.condicoes_trabalho?.horario === 'flexivel' ? 'Flexível' : cargo.condicoes_trabalho?.horario === 'turnos' ? 'Turnos' : 'Não especificado'}</div>
                  </div>
                </div>
                <div class="condition-item">
                  <span class="condition-icon">✈️</span>
                  <div>
                    <div class="condition-label">Viagens</div>
                    <div class="condition-value">${cargo.condicoes_trabalho?.viagens ? 'Sim' : 'Não'}</div>
                  </div>
                </div>
                <div class="condition-item">
                  <span class="condition-icon">💻</span>
                  <div>
                    <div class="condition-label">Home Office</div>
                    <div class="condition-value">${cargo.condicoes_trabalho?.home_office ? 'Permitido' : 'Não permitido'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Footer -->
          <div class="footer">
            <div>
              <strong>Versão:</strong> ${cargo.versao || '1.0'} | 
              <strong>Criado em:</strong> ${formatDate(cargo.created_at)}
            </div>
            <div>
              Documento gerado automaticamente pelo Sistema DCF
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Função para download DCF como PDF
  const handlePrintDCF = async (cargo) => {
    try {
      // Buscar dados completos do cargo se necessário
      const token = localStorage.getItem('token');
      let fullCargo = cargo;
      
      if (!cargo.missao_proposito) {
        const response = await axios.get(`${API}/api/dcf/templates/${cargo.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fullCargo = response.data.template || cargo;
      }
      
      // Criar PDF
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;
      
      // Helper para adicionar nova página se necessário
      const checkNewPage = (neededHeight = 20) => {
        if (yPos + neededHeight > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };
      
      // Helper para obter label do nível
      const getNivelLabel = (nivel) => {
        const niveis = {
          'trainee': 'Trainee',
          'junior': 'Júnior',
          'pleno': 'Pleno',
          'senior': 'Sênior',
          'coordenacao': 'Coordenação',
          'gerencia': 'Gerência',
          'diretoria': 'Diretoria'
        };
        return niveis[nivel] || nivel || 'N/A';
      };
      
      // Helper para obter nome da competência
      const getCompetenciaNome = (compId) => {
        const comp = competencias.find(c => c.id === compId);
        return comp ? comp.nome : compId;
      };
      
      // Header com fundo azul
      doc.setFillColor(30, 64, 175);
      doc.rect(0, 0, pageWidth, 35, 'F');
      
      // Título
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('DESCRIÇÃO DE CARGO E FUNÇÃO', margin, 15);
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text(fullCargo.nome_cargo || 'Cargo não informado', margin, 25);
      
      if (fullCargo.codigo_cargo) {
        doc.setFontSize(10);
        doc.text(`Código: ${fullCargo.codigo_cargo}`, margin, 32);
      }
      
      yPos = 45;
      
      // Info bar
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, yPos, contentWidth, 15, 'F');
      
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(8);
      doc.text('DEPARTAMENTO', margin + 5, yPos + 5);
      doc.text('NÍVEL', margin + 50, yPos + 5);
      doc.text('CBO', margin + 95, yPos + 5);
      doc.text('STATUS', margin + 140, yPos + 5);
      
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(fullCargo.departamento || 'N/A', margin + 5, yPos + 12);
      doc.text(getNivelLabel(fullCargo.nivel_hierarquico), margin + 50, yPos + 12);
      doc.text(fullCargo.cbo || 'N/A', margin + 95, yPos + 12);
      doc.text(fullCargo.status === 'ativo' ? 'Ativo' : 'Inativo', margin + 140, yPos + 12);
      
      yPos += 25;
      
      // Seção: Missão/Propósito
      if (fullCargo.missao_proposito || fullCargo.objetivo_cargo) {
        doc.setFillColor(240, 253, 244);
        doc.setDrawColor(34, 197, 94);
        doc.rect(margin, yPos, contentWidth, 25, 'FD');
        
        doc.setTextColor(30, 64, 175);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('MISSÃO / PROPÓSITO DO CARGO', margin + 5, yPos + 7);
        
        doc.setTextColor(22, 101, 52);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const missao = fullCargo.missao_proposito || fullCargo.objetivo_cargo || 'Não informado';
        const missaoLines = doc.splitTextToSize(missao, contentWidth - 10);
        doc.text(missaoLines.slice(0, 2), margin + 5, yPos + 15);
        
        yPos += 30;
      }
      
      // Seção: Atividades
      if (fullCargo.atividades && fullCargo.atividades.length > 0) {
        checkNewPage(40);
        
        doc.setTextColor(30, 64, 175);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('PRINCIPAIS ATIVIDADES', margin, yPos + 5);
        
        doc.setDrawColor(219, 234, 254);
        doc.line(margin, yPos + 8, margin + 80, yPos + 8);
        
        yPos += 15;
        
        fullCargo.atividades.forEach((ativ, idx) => {
          checkNewPage(15);
          
          // Número
          doc.setFillColor(59, 130, 246);
          doc.circle(margin + 4, yPos + 3, 3, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(8);
          doc.text(String(idx + 1), margin + 2.5, yPos + 4.5);
          
          // Descrição
          doc.setTextColor(51, 65, 85);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          const descLines = doc.splitTextToSize(ativ.descricao || 'N/A', contentWidth - 20);
          doc.text(descLines[0], margin + 12, yPos + 4);
          
          // Tipo e percentual
          if (ativ.tipo || ativ.percentual_tempo) {
            doc.setTextColor(100, 116, 139);
            doc.setFontSize(7);
            const info = [];
            if (ativ.tipo) info.push(ativ.tipo);
            if (ativ.percentual_tempo) info.push(`${ativ.percentual_tempo}% do tempo`);
            doc.text(info.join(' | '), margin + 12, yPos + 9);
          }
          
          yPos += 14;
        });
        
        yPos += 5;
      }
      
      // Seção: Requisitos
      if (fullCargo.requisitos && fullCargo.requisitos.length > 0) {
        checkNewPage(40);
        
        doc.setTextColor(30, 64, 175);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('REQUISITOS', margin, yPos + 5);
        
        doc.setDrawColor(219, 234, 254);
        doc.line(margin, yPos + 8, margin + 40, yPos + 8);
        
        yPos += 15;
        
        fullCargo.requisitos.forEach((req) => {
          checkNewPage(12);
          
          if (req.obrigatorio) {
            doc.setFillColor(239, 68, 68);
          } else {
            doc.setFillColor(34, 197, 94);
          }
          doc.circle(margin + 3, yPos + 2, 2, 'F');
          
          doc.setTextColor(51, 65, 85);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          
          const tipoLabel = {
            'formacao': 'Formação',
            'experiencia': 'Experiência',
            'certificacao': 'Certificação',
            'idioma': 'Idioma',
            'outro': 'Outro'
          }[req.tipo] || req.tipo;
          
          doc.text(`${tipoLabel}: ${req.descricao || 'N/A'}`, margin + 10, yPos + 3);
          
          yPos += 10;
        });
        
        yPos += 5;
      }
      
      // Seção: Competências Técnicas
      const allCompetencias = [
        ...(fullCargo.competencias_tecnicas || []),
        ...(fullCargo.competencias_obrigatorias?.tecnicas || [])
      ];
      
      if (allCompetencias.length > 0) {
        checkNewPage(30);
        
        doc.setTextColor(30, 64, 175);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('COMPETÊNCIAS TÉCNICAS', margin, yPos + 5);
        
        doc.setDrawColor(219, 234, 254);
        doc.line(margin, yPos + 8, margin + 70, yPos + 8);
        
        yPos += 15;
        
        allCompetencias.forEach((comp) => {
          checkNewPage(10);
          const nome = typeof comp === 'string' ? getCompetenciaNome(comp) : (comp.nome || comp);
          doc.setTextColor(51, 65, 85);
          doc.setFontSize(9);
          doc.text(`• ${nome}`, margin + 5, yPos + 3);
          yPos += 8;
        });
        
        yPos += 5;
      }
      
      // Seção: Competências Comportamentais
      const compComportamentais = [
        ...(fullCargo.competencias_comportamentais || []),
        ...(fullCargo.competencias_obrigatorias?.comportamentais || [])
      ];
      
      if (compComportamentais.length > 0) {
        checkNewPage(30);
        
        doc.setTextColor(30, 64, 175);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('COMPETÊNCIAS COMPORTAMENTAIS', margin, yPos + 5);
        
        doc.setDrawColor(219, 234, 254);
        doc.line(margin, yPos + 8, margin + 90, yPos + 8);
        
        yPos += 15;
        
        compComportamentais.forEach((comp) => {
          checkNewPage(10);
          const nome = typeof comp === 'string' ? getCompetenciaNome(comp) : (comp.nome || comp);
          doc.setTextColor(51, 65, 85);
          doc.setFontSize(9);
          doc.text(`• ${nome}`, margin + 5, yPos + 3);
          yPos += 8;
        });
        
        yPos += 5;
      }
      
      // Seção: Condições de Trabalho
      if (fullCargo.condicoes_trabalho) {
        checkNewPage(30);
        
        doc.setTextColor(30, 64, 175);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('CONDIÇÕES DE TRABALHO', margin, yPos + 5);
        
        doc.setDrawColor(219, 234, 254);
        doc.line(margin, yPos + 8, margin + 70, yPos + 8);
        
        yPos += 15;
        
        const cond = fullCargo.condicoes_trabalho;
        doc.setTextColor(51, 65, 85);
        doc.setFontSize(9);
        
        if (cond.ambiente) {
          doc.text(`• Ambiente: ${cond.ambiente}`, margin + 5, yPos + 3);
          yPos += 8;
        }
        if (cond.horario) {
          doc.text(`• Horário: ${cond.horario}`, margin + 5, yPos + 3);
          yPos += 8;
        }
        if (cond.viagens !== undefined) {
          doc.text(`• Viagens: ${cond.viagens ? 'Sim' : 'Não'}`, margin + 5, yPos + 3);
          yPos += 8;
        }
        if (cond.home_office !== undefined) {
          doc.text(`• Home Office: ${cond.home_office ? 'Sim' : 'Não'}`, margin + 5, yPos + 3);
          yPos += 8;
        }
      }
      
      // Cargo Sensível
      if (fullCargo.cargo_sensivel) {
        checkNewPage(20);
        yPos += 5;
        
        doc.setFillColor(254, 243, 199);
        doc.setDrawColor(245, 158, 11);
        doc.rect(margin, yPos, contentWidth, 15, 'FD');
        
        doc.setTextColor(146, 64, 14);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('⚠️ CARGO SENSÍVEL', margin + 5, yPos + 7);
        
        if (fullCargo.grau_sensibilidade) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.text(`Grau de Sensibilidade: ${fullCargo.grau_sensibilidade}`, margin + 5, yPos + 12);
        }
      }
      
      // Footer
      const footerY = pageHeight - 10;
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(8);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, footerY);
      doc.text('GridProfile - Sistema de RH', pageWidth - margin - 45, footerY);
      
      // Salvar PDF
      const fileName = `DCF_${(fullCargo.nome_cargo || 'cargo').replace(/\s+/g, '_')}_${fullCargo.codigo_cargo || 'sem_codigo'}.pdf`;
      doc.save(fileName);
      
      alert('✅ PDF da DCF baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF da DCF:', error);
      alert('❌ Erro ao gerar PDF: ' + (error.message || 'Erro desconhecido'));
    }
  };

  // Função para download DCF como HTML
  const handleDownloadDCF = async (cargo) => {
    try {
      // Buscar dados completos do cargo se necessário
      const token = localStorage.getItem('token');
      let fullCargo = cargo;
      
      if (!cargo.missao_proposito) {
        const response = await axios.get(`${API}/api/dcf/templates/${cargo.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fullCargo = response.data.template || cargo;
      }
      
      const htmlContent = generateDCFDocument(fullCargo);
      
      // Criar blob e fazer download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DCF_${fullCargo.nome_cargo?.replace(/\s+/g, '_') || 'cargo'}_${fullCargo.codigo_cargo || 'sem_codigo'}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert('✅ DCF baixada com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar DCF:', error);
      alert('❌ Erro ao gerar arquivo para download: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleViewCargo = async (cargoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/dcf/templates/${cargoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.template) {
        setCargoSelecionado(response.data.template);
        setShowViewModal(true);
      }
    } catch (error) {
      console.error('Erro ao buscar cargo:', error);
      alert('❌ Erro ao carregar detalhes do cargo');
    }
  };

  const handleEditCargo = async (cargoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/dcf/templates/${cargoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.template) {
        const cargo = response.data.template;
        
        // Preencher formData com os dados do cargo
        setFormData({
          nome_cargo: cargo.nome_cargo || '',
          codigo_cargo: cargo.codigo_cargo || '',
          cbo: cargo.cbo || '',
          departamento: cargo.departamento || '',
          nivel_hierarquico: cargo.nivel_hierarquico || 'junior',
          superior_imediato: cargo.superior_imediato || '',
          missao_proposito: cargo.objetivo_cargo || cargo.missao_proposito || '',
          atividades: cargo.atividades && cargo.atividades.length > 0 
            ? cargo.atividades 
            : [{ descricao: '', tipo: 'essencial', percentual_tempo: 70 }],
          competencias_tecnicas: cargo.competencias_tecnicas || [],
          competencias_comportamentais: cargo.competencias_comportamentais || [],
          competencias_gerenciais: cargo.competencias_gerenciais || [],
          competencias_obrigatorias: cargo.competencias_obrigatorias || {
            tecnicas: [],
            comportamentais: [],
            gerenciais: []
          },
          requisitos: cargo.requisitos && cargo.requisitos.length > 0 
            ? cargo.requisitos 
            : [{ tipo: 'formacao', descricao: '', obrigatorio: true, detalhes: '' }],
          condicoes_trabalho: cargo.condicoes_trabalho || {
            ambiente: 'escritorio',
            horario: 'comercial',
            viagens: false,
            home_office: false
          }
        });
        
        setIsEditMode(true);
        setCargoIdEditando(cargoId);
        setShowCreateModal(true);
      }
    } catch (error) {
      console.error('Erro ao buscar cargo:', error);
      alert('❌ Erro ao carregar cargo para edição');
    }
  };

  const handleDeleteCargo = async (cargoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este cargo? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/dcf/templates/${cargoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('✅ Cargo excluído com sucesso!');
      loadCargos();
    } catch (error) {
      console.error('Erro ao excluir cargo:', error);
      alert('❌ Erro ao excluir cargo. Tente novamente.');
    }
  };

  const addAtividade = (tipo = 'essencial') => {
    setFormData(prev => ({
      ...prev,
      atividades: [...prev.atividades, { descricao: '', tipo, percentual_tempo: 0 }]
    }));
  };

  const updateAtividade = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      atividades: prev.atividades.map((ativ, i) => 
        i === index ? { ...ativ, [field]: value } : ativ
      )
    }));
  };

  const removeAtividade = (index) => {
    setFormData(prev => ({
      ...prev,
      atividades: prev.atividades.filter((_, i) => i !== index)
    }));
  };

  const addRequisito = () => {
    setFormData(prev => ({
      ...prev,
      requisitos: [...prev.requisitos, { tipo: 'formacao', descricao: '', obrigatorio: true, detalhes: '' }]
    }));
  };

  const updateRequisito = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      requisitos: prev.requisitos.map((req, i) => 
        i === index ? { ...req, [field]: value } : req
      )
    }));
  };

  const removeRequisito = (index) => {
    setFormData(prev => ({
      ...prev,
      requisitos: prev.requisitos.filter((_, i) => i !== index)
    }));
  };

  const toggleCompetencia = (tipo, competenciaId) => {
    const field = `competencias_${tipo}`;
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(competenciaId)
        ? prev[field].filter(id => id !== competenciaId)
        : [...prev[field], competenciaId]
    }));
  };

  const setCompetenciaObrigatoria = (tipo, competenciaId, obrigatoria) => {
    setFormData(prev => ({
      ...prev,
      competencias_obrigatorias: {
        ...prev.competencias_obrigatorias,
        [tipo]: obrigatoria 
          ? [...(prev.competencias_obrigatorias[tipo] || []), competenciaId].filter((id, index, arr) => arr.indexOf(id) === index)
          : (prev.competencias_obrigatorias[tipo] || []).filter(id => id !== competenciaId)
      }
    }));
  };

  const isCompetenciaObrigatoria = (tipo, competenciaId) => {
    return (formData.competencias_obrigatorias[tipo] || []).includes(competenciaId);
  };

  const getTotalPercentual = () => {
    return formData.atividades.reduce((total, ativ) => total + (ativ.percentual_tempo || 0), 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ativo': return 'bg-green-100 text-green-800';
      case 'inativo': return 'bg-gray-100 text-gray-800';
      case 'em_revisao': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ativo': return 'Ativo';
      case 'inativo': return 'Inativo';
      case 'em_revisao': return 'Em revisão';
      default: return status || 'Sem status';
    }
  };

  const getNivelLabel = (nivel) => {
    const nivelEncontrado = niveisHierarquicos.find((item) => item.value === nivel);
    return nivelEncontrado?.label || nivel || 'Não definido';
  };

  const filteredCargos = cargos.filter(cargo => {
    const matchesSearch = cargo.nome_cargo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cargo.codigo_cargo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesNivel = !filterNivel || cargo.nivel_hierarquico === filterNivel;
    const matchesDepartamento = !filterDepartamento || cargo.departamento === filterDepartamento;
    
    return matchesSearch && matchesNivel && matchesDepartamento;
  });

  const cargosEnriquecidos = filteredCargos.map((cargo) => {
    const totalCompetencias = [
      ...(cargo.competencias_tecnicas || []),
      ...(cargo.competencias_comportamentais || []),
      ...(cargo.competencias_gerenciais || [])
    ].filter(Boolean).length;

    return {
      ...cargo,
      totalCompetencias,
      funcionariosVinculados: Number(cargo.funcionarios_vinculados || 0)
    };
  });

  const totalCargos = cargos.length;
  const cargosAtivos = cargos.filter((cargo) => cargo.status === 'ativo').length;
  const cargosSemCompetencias = cargos.filter((cargo) => {
    const totalCompetencias = [
      ...(cargo.competencias_tecnicas || []),
      ...(cargo.competencias_comportamentais || []),
      ...(cargo.competencias_gerenciais || [])
    ].filter(Boolean).length;
    return totalCompetencias === 0;
  }).length;
  const cargosSemVinculos = cargos.filter((cargo) => Number(cargo.funcionarios_vinculados || 0) === 0).length;

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <section className="overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-blue-900 text-white shadow-xl">
        <div className="grid gap-8 p-6 lg:grid-cols-[1.5fr_1fr] lg:p-8">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-blue-100">
              <Briefcase className="h-3.5 w-3.5" />
              Gerenciar Cargos
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight lg:text-4xl">O cargo precisa nascer completo, não virar retrabalho depois.</h1>
              <p className="mt-3 max-w-2xl text-sm text-slate-200 lg:text-base">
                Esta área organiza a estrutura oficial do DCF. Aqui você cria, revisa, publica e exporta cargos com competências, atividades, requisitos e condições operacionais.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <button
                type="button"
                onClick={openCreateModal}
                className="rounded-2xl bg-white px-4 py-4 text-left text-slate-900 transition-transform hover:-translate-y-0.5"
              >
                <Plus className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Novo cargo</p>
                <p className="mt-1 text-sm text-slate-600">Abrir o formulário completo de DCF para cadastro.</p>
              </button>
              <button
                type="button"
                onClick={() => setFilterNivel('senior')}
                className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 text-left hover:bg-white/15"
              >
                <TrendingUp className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Sênior e gestão</p>
                <p className="mt-1 text-sm text-slate-200">Focar cargos críticos e de maior impacto.</p>
              </button>
              <button
                type="button"
                onClick={() => setFilterDepartamento('Tecnologia')}
                className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 text-left hover:bg-white/15"
              >
                <Building className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Filtrar área</p>
                <p className="mt-1 text-sm text-slate-200">Entrar rapidamente em um departamento específico.</p>
              </button>
              <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4">
                <Clock className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Sem vínculo</p>
                <p className="mt-1 text-sm text-slate-200">{cargosSemVinculos} cargo(s) ainda sem colaborador associado.</p>
              </div>
            </div>
          </div>

          <div className="rounded-[24px] border border-white/10 bg-white/10 p-5 backdrop-blur">
            <p className="text-sm font-medium text-slate-200">Panorama da estrutura</p>
            <div className="mt-5 space-y-3 text-sm text-slate-100">
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Total de cargos</span>
                <strong>{totalCargos}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Ativos</span>
                <strong>{cargosAtivos}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Sem competências</span>
                <strong>{cargosSemCompetencias}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-blue-300/20 px-4 py-3 text-blue-100">
                <span>Resultado do filtro</span>
                <strong>{cargosEnriquecidos.length}</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Cargos cadastrados</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{totalCargos}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Ativos</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{cargosAtivos}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Sem competências</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{cargosSemCompetencias}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Sem vínculo</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{cargosSemVinculos}</p>
        </article>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Triagem</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">Buscar e segmentar cargos</h2>
            <p className="mt-2 text-sm text-slate-600">Aplique busca por nome ou código e recorte por nível e departamento para navegar pela estrutura com menos ruído.</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setFilterNivel('');
              setFilterDepartamento('');
            }}
            className="inline-flex items-center justify-center rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Limpar filtros
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
              <Search className="h-4 w-4 text-slate-400" />
              Buscar por cargo ou código
            </label>
            <input
              type="text"
              placeholder="Ex: analista, RH-001..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
              <Filter className="h-4 w-4 text-slate-400" />
              Nível hierárquico
            </label>
            <select
              value={filterNivel}
              onChange={(e) => setFilterNivel(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os níveis</option>
              {niveisHierarquicos.map(nivel => (
                <option key={nivel.value} value={nivel.value}>{nivel.label}</option>
              ))}
            </select>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
              <Building className="h-4 w-4 text-slate-400" />
              Departamento
            </label>
            <select
              value={filterDepartamento}
              onChange={(e) => setFilterDepartamento(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os departamentos</option>
              {departamentos.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-5 xl:grid-cols-2 2xl:grid-cols-3">
        {cargosEnriquecidos.map((cargo) => (
          <article key={cargo.id} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-transform hover:-translate-y-0.5">
            <div className="border-b border-slate-200 bg-slate-50 p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-blue-100 p-3">
                    <Briefcase className="h-5 w-5 text-blue-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{cargo.nome_cargo}</h3>
                    <p className="text-sm text-slate-500">{cargo.codigo_cargo || 'Sem código'}</p>
                  </div>
                </div>
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusColor(cargo.status)}`}>
                  {getStatusLabel(cargo.status)}
                </span>
              </div>
            </div>

            <div className="space-y-4 p-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Nível</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{getNivelLabel(cargo.nivel_hierarquico)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Departamento</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{cargo.departamento || 'Não informado'}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Vinculados</p>
                  <div className="mt-1 flex items-center gap-1 text-slate-900">
                    <Users className="h-4 w-4 text-slate-400" />
                    <span className="text-sm font-medium">{cargo.funcionariosVinculados}</span>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Competências</p>
                  <div className="mt-1 flex items-center gap-1 text-slate-900">
                    <Target className="h-4 w-4 text-slate-400" />
                    <span className="text-sm font-medium">{cargo.totalCompetencias}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleViewCargo(cargo.id)}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  title="Visualizar"
                >
                  <Eye className="h-4 w-4" />
                  Visualizar
                </button>
                <button
                  onClick={() => handleEditCargo(cargo.id)}
                  className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
                  title="Editar"
                >
                  <Edit className="h-4 w-4" />
                  Editar
                </button>
                <button
                  onClick={() => handlePrintDCF(cargo)}
                  className="inline-flex items-center gap-2 rounded-full border border-purple-200 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50"
                  title="Imprimir DCF"
                >
                  <Printer className="h-4 w-4" />
                  PDF
                </button>
                <button
                  onClick={() => handleDownloadDCF(cargo)}
                  className="inline-flex items-center gap-2 rounded-full border border-cyan-200 px-3 py-2 text-sm font-medium text-cyan-700 hover:bg-cyan-50"
                  title="Download DCF"
                >
                  <Download className="h-4 w-4" />
                  HTML
                </button>
                <button
                  onClick={() => handleDeleteCargo(cargo.id)}
                  className="inline-flex items-center gap-2 rounded-full border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                  title="Excluir"
                >
                  <Trash2 className="h-4 w-4" />
                  Excluir
                </button>
              </div>
            </div>
          </article>
        ))}

        {cargosEnriquecidos.length === 0 && (
          <div className="col-span-full rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center shadow-sm">
            <Briefcase className="mx-auto h-14 w-14 text-slate-300" />
            <p className="mt-4 text-lg font-medium text-slate-700">Nenhum cargo encontrado</p>
            <p className="mt-2 text-sm text-slate-500">Ajuste os filtros ou cadastre uma nova DCF.</p>
          </div>
        )}
      </section>

      {/* Modal de Criação de Cargo */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
          <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50 shadow-2xl">
            <div className="border-b border-slate-800 bg-gradient-to-r from-slate-950 via-slate-900 to-blue-900 px-6 py-5 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.24em] text-blue-100">Formulário DCF</p>
                  <h2 className="mt-2 text-2xl font-semibold">
                {isEditMode ? '✏️ Editar Cargo DCF' : '➕ Criar Nova DCF'}
                  </h2>
                  <p className="mt-1 text-sm text-slate-200">Preencha a estrutura oficial do cargo com identificação, responsabilidades, competências e requisitos.</p>
                </div>
              <button 
                onClick={closeCreateModal}
                  className="text-white/80 hover:text-white"
              >
                ✕
              </button>
              </div>
            </div>

            <form onSubmit={handleCreateCargo} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="space-y-6 px-4 py-5 md:px-6">
              {/* Identificação do Cargo */}
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="rounded-2xl bg-blue-100 p-3">
                    <Briefcase className="h-5 w-5 text-blue-700" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">Base do Cargo</p>
                    <h3 className="text-lg font-semibold text-slate-900">Identificação do cargo</h3>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Cargo*
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nome_cargo}
                      onChange={(e) => setFormData({...formData, nome_cargo: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Analista de RH Senior"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Departamento*
                    </label>
                    <select
                      required
                      value={formData.departamento}
                      onChange={(e) => {
                        const dept = e.target.value;
                        
                        // Gerar código automaticamente ao selecionar departamento (se ainda não tiver código)
                        if (dept && !formData.codigo_cargo) {
                          const sigla = dept.substring(0, 3).toUpperCase();
                          const numero = String(Math.floor(Math.random() * 900) + 100);
                          const codigoGerado = `${sigla}-${numero}`;
                          setFormData({...formData, departamento: dept, codigo_cargo: codigoGerado});
                        } else {
                          setFormData({...formData, departamento: dept});
                        }
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione o departamento</option>
                      {departamentos.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                      Código do Cargo
                      <span className="text-xs text-gray-500">(gerado automaticamente)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.codigo_cargo}
                      onChange={(e) => setFormData({...formData, codigo_cargo: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                      placeholder="Selecione um departamento primeiro"
                      readOnly={!formData.departamento}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 O código é gerado automaticamente, mas você pode editá-lo se desejar
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                      CBO (Classificação Brasileira de Ocupações)
                      <button
                        type="button"
                        onClick={sugerirCBO}
                        disabled={sugerindoCbo || !formData.nome_cargo}
                        className="text-xs bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        title="Sugerir CBO com IA"
                      >
                        {sugerindoCbo ? (
                          <>⏳ Sugerindo...</>
                        ) : (
                          <>✨ Sugerir com IA</>
                        )}
                      </button>
                    </label>
                    <input
                      type="text"
                      value={formData.cbo}
                      onChange={(e) => setFormData({...formData, cbo: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: 2124-05"
                      maxLength="7"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Formato: XXXX-XX (ex: 2124-05). Clique em "Sugerir com IA" ou digite manualmente
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nível Hierárquico*
                    </label>
                    <select
                      required
                      value={formData.nivel_hierarquico}
                      onChange={(e) => setFormData({...formData, nivel_hierarquico: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {niveisHierarquicos.map(nivel => (
                        <option key={nivel.value} value={nivel.value}>{nivel.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cargo Superior Imediato
                    </label>
                    <select
                      value={formData.superior_imediato}
                      onChange={(e) => setFormData({...formData, superior_imediato: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione o cargo superior</option>
                      {cargos
                        .filter(cargo => cargo.id !== formData.id) // Não mostrar o próprio cargo na lista
                        .map(cargo => (
                          <option key={cargo.id} value={cargo.id}>
                            {cargo.nome_cargo} - {cargo.departamento} ({cargo.nivel_hierarquico})
                          </option>
                        ))
                      }
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Ex: "Gerente Comercial" responde ao cargo "Diretor Comercial"
                    </p>
                  </div>
                  
                  {/* Cargo Sensível */}
                  <div className="md:col-span-2 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="cargo_sensivel"
                        checked={formData.cargo_sensivel || false}
                        onChange={(e) => setFormData({
                          ...formData, 
                          cargo_sensivel: e.target.checked,
                          grau_sensibilidade: e.target.checked ? (formData.grau_sensibilidade || 'baixo') : ''
                        })}
                        className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer"
                      />
                      <label htmlFor="cargo_sensivel" className="text-sm font-medium text-amber-800 cursor-pointer flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Cargo Sensível
                      </label>
                    </div>
                    <p className="text-xs text-amber-700 mt-2 ml-8">
                      Marque se este cargo lida com informações confidenciais, dados sensíveis ou tem impacto crítico na organização.
                    </p>
                    
                    {/* Campo Grau - só aparece se Cargo Sensível estiver marcado */}
                    {formData.cargo_sensivel && (
                      <div className="mt-4 ml-8">
                        <label className="block text-sm font-medium text-amber-800 mb-2">
                          Grau de Sensibilidade
                        </label>
                        <select
                          value={formData.grau_sensibilidade || 'baixo'}
                          onChange={(e) => setFormData({...formData, grau_sensibilidade: e.target.value})}
                          className="w-full md:w-1/2 border border-amber-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                        >
                          <option value="baixo">🟢 Baixo</option>
                          <option value="medio">🟡 Médio</option>
                          <option value="alto">🟠 Alto</option>
                          <option value="critico">🔴 Crítico</option>
                        </select>
                        <p className="text-xs text-amber-600 mt-1">
                          {formData.grau_sensibilidade === 'baixo' && '• Acesso limitado a informações de baixa confidencialidade'}
                          {formData.grau_sensibilidade === 'medio' && '• Acesso a informações de média confidencialidade e processos internos'}
                          {formData.grau_sensibilidade === 'alto' && '• Acesso a informações estratégicas e dados confidenciais'}
                          {formData.grau_sensibilidade === 'critico' && '• Acesso irrestrito a informações críticas e decisões estratégicas'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Missão/Propósito */}
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="rounded-2xl bg-emerald-100 p-3">
                    <Target className="h-5 w-5 text-emerald-700" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">Essência</p>
                    <h3 className="text-lg font-semibold text-slate-900">Missão e propósito</h3>
                  </div>
                </div>
                <textarea
                  required
                  value={formData.missao_proposito}
                  onChange={(e) => setFormData({...formData, missao_proposito: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Descreva a razão de existir deste cargo e sua contribuição para os objetivos organizacionais..."
                />
              </div>

              {/* Principais Atividades */}
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="rounded-2xl bg-amber-100 p-3">
                    <Clock className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">Execução</p>
                    <h3 className="text-lg font-semibold text-slate-900">Principais atividades</h3>
                  </div>
                </div>
                
                {/* Atividades Essenciais */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">Essenciais (70-80%):</h4>
                    <button
                      type="button"
                      onClick={() => addAtividade('essencial')}
                      className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-lg hover:bg-blue-200"
                    >
                      + Adicionar Atividade Essencial
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.atividades
                      .filter(ativ => ativ.tipo === 'essencial')
                      .map((atividade, index) => (
                        <div key={`essencial-${index}`} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">•</span>
                          <input
                            type="text"
                            value={atividade.descricao}
                            onChange={(e) => updateAtividade(index, 'descricao', e.target.value)}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Descreva a atividade essencial..."
                          />
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={atividade.percentual_tempo || ''}
                            onChange={(e) => updateAtividade(index, 'percentual_tempo', parseFloat(e.target.value))}
                            className="w-16 border border-gray-300 rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="%"
                          />
                          <button
                            type="button"
                            onClick={() => removeAtividade(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Atividades Complementares */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">Complementares (15-25%):</h4>
                    <button
                      type="button"
                      onClick={() => addAtividade('complementar')}
                      className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-lg hover:bg-green-200"
                    >
                      + Adicionar Atividade Complementar
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.atividades
                      .map((ativ, realIndex) => ({ ativ, realIndex }))
                      .filter(({ ativ }) => ativ.tipo === 'complementar')
                      .map(({ ativ: atividade, realIndex }) => (
                        <div key={`complementar-${realIndex}`} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">•</span>
                          <input
                            type="text"
                            value={atividade.descricao}
                            onChange={(e) => updateAtividade(realIndex, 'descricao', e.target.value)}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Descreva a atividade complementar..."
                          />
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={atividade.percentual_tempo || ''}
                            onChange={(e) => updateAtividade(realIndex, 'percentual_tempo', parseFloat(e.target.value))}
                            className="w-16 border border-gray-300 rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="%"
                          />
                          <button
                            type="button"
                            onClick={() => removeAtividade(realIndex)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Atividades Eventuais */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">Eventuais (5%):</h4>
                    <button
                      type="button"
                      onClick={() => addAtividade('eventual')}
                      className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-lg hover:bg-purple-200"
                    >
                      + Adicionar Atividade Eventual
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.atividades
                      .map((ativ, realIndex) => ({ ativ, realIndex }))
                      .filter(({ ativ }) => ativ.tipo === 'eventual')
                      .map(({ ativ: atividade, realIndex }) => (
                        <div key={`eventual-${realIndex}`} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">•</span>
                          <input
                            type="text"
                            value={atividade.descricao}
                            onChange={(e) => updateAtividade(realIndex, 'descricao', e.target.value)}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Descreva a atividade eventual..."
                          />
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={atividade.percentual_tempo || ''}
                            onChange={(e) => updateAtividade(realIndex, 'percentual_tempo', parseFloat(e.target.value))}
                            className="w-16 border border-gray-300 rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="%"
                          />
                          <button
                            type="button"
                            onClick={() => removeAtividade(realIndex)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Resumo de Percentuais */}
                <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                  <div className="text-sm">
                    <span className="font-medium">Total de tempo alocado: </span>
                    <span className={`font-bold ${getTotalPercentual() !== 100 ? 'text-red-600' : 'text-green-600'}`}>
                      {getTotalPercentual()}%
                    </span>
                    {getTotalPercentual() !== 100 && (
                      <span className="text-red-500 ml-2">⚠️ O total deve ser 100%</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Competências Necessárias */}
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="rounded-2xl bg-violet-100 p-3">
                    <Target className="h-5 w-5 text-violet-700" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">Mapa de Competências</p>
                    <h3 className="text-lg font-semibold text-slate-900">Competências necessárias</h3>
                  </div>
                </div>
                
                {/* Competências Técnicas */}
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Competências Técnicas:</h4>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg">
                    {competencias
                      .filter(comp => comp.tipo === 'tecnica')
                      .map((competencia) => (
                        <label 
                          key={`tecnica-${competencia.id}`}
                          className="flex items-center space-x-3 p-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.competencias_tecnicas.includes(competencia.id)}
                            onChange={() => toggleCompetencia('tecnicas', competencia.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900">{competencia.nome}</span>
                            <div className="flex space-x-2 mt-1">
                              <button
                                type="button"
                                onClick={() => setCompetenciaObrigatoria('tecnicas', competencia.id, true)}
                                className={`text-xs px-2 py-1 rounded ${
                                  isCompetenciaObrigatoria('tecnicas', competencia.id) 
                                    ? 'bg-yellow-200 text-yellow-800' 
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                ⭐ Obrigatório
                              </button>
                              <button
                                type="button"
                                onClick={() => setCompetenciaObrigatoria('tecnicas', competencia.id, false)}
                                className={`text-xs px-2 py-1 rounded ${
                                  !isCompetenciaObrigatoria('tecnicas', competencia.id) && formData.competencias_tecnicas.includes(competencia.id)
                                    ? 'bg-orange-200 text-orange-800' 
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                🔶 Desejável
                              </button>
                            </div>
                          </div>
                        </label>
                      ))}
                  </div>
                </div>

                {/* Competências Comportamentais */}
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Competências Comportamentais:</h4>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg">
                    {competencias
                      .filter(comp => comp.tipo === 'comportamental')
                      .map((competencia) => (
                        <label 
                          key={`comportamental-${competencia.id}`}
                          className="flex items-center space-x-3 p-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.competencias_comportamentais.includes(competencia.id)}
                            onChange={() => toggleCompetencia('comportamentais', competencia.id)}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900">{competencia.nome}</span>
                            <div className="flex space-x-2 mt-1">
                              <button
                                type="button"
                                onClick={() => setCompetenciaObrigatoria('comportamentais', competencia.id, true)}
                                className={`text-xs px-2 py-1 rounded ${
                                  isCompetenciaObrigatoria('comportamentais', competencia.id) 
                                    ? 'bg-yellow-200 text-yellow-800' 
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                ⭐ Obrigatório
                              </button>
                              <button
                                type="button"
                                onClick={() => setCompetenciaObrigatoria('comportamentais', competencia.id, false)}
                                className={`text-xs px-2 py-1 rounded ${
                                  !isCompetenciaObrigatoria('comportamentais', competencia.id) && formData.competencias_comportamentais.includes(competencia.id)
                                    ? 'bg-orange-200 text-orange-800' 
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                🔶 Desejável
                              </button>
                            </div>
                          </div>
                        </label>
                      ))}
                  </div>
                </div>

                {/* Competências Gerenciais */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Competências Gerenciais:</h4>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg">
                    {competencias
                      .filter(comp => comp.tipo === 'gerencial')
                      .map((competencia) => (
                        <label 
                          key={`gerencial-${competencia.id}`}
                          className="flex items-center space-x-3 p-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.competencias_gerenciais.includes(competencia.id)}
                            onChange={() => toggleCompetencia('gerenciais', competencia.id)}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900">{competencia.nome}</span>
                            <div className="flex space-x-2 mt-1">
                              <button
                                type="button"
                                onClick={() => setCompetenciaObrigatoria('gerenciais', competencia.id, true)}
                                className={`text-xs px-2 py-1 rounded ${
                                  isCompetenciaObrigatoria('gerenciais', competencia.id) 
                                    ? 'bg-yellow-200 text-yellow-800' 
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                ⭐ Obrigatório
                              </button>
                              <button
                                type="button"
                                onClick={() => setCompetenciaObrigatoria('gerenciais', competencia.id, false)}
                                className={`text-xs px-2 py-1 rounded ${
                                  !isCompetenciaObrigatoria('gerenciais', competencia.id) && formData.competencias_gerenciais.includes(competencia.id)
                                    ? 'bg-orange-200 text-orange-800' 
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                🔶 Desejável
                              </button>
                            </div>
                          </div>
                        </label>
                      ))}
                  </div>
                </div>
              </div>

              {/* Requisitos */}
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-rose-100 p-3">
                      <FileText className="h-5 w-5 text-rose-700" />
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">Critérios</p>
                      <h3 className="text-lg font-semibold text-slate-900">Requisitos</h3>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addRequisito}
                    className="rounded-full bg-rose-100 px-3 py-1 text-sm text-rose-700 hover:bg-rose-200"
                  >
                    + Adicionar Requisito
                  </button>
                </div>
                
                <div className="space-y-4">
                  {formData.requisitos.map((requisito, index) => (
                    <div key={index} className="border border-red-200 rounded-lg p-3 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                        <select
                          value={requisito.tipo}
                          onChange={(e) => updateRequisito(index, 'tipo', e.target.value)}
                          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="formacao">Formação</option>
                          <option value="experiencia">Experiência</option>
                          <option value="certificacao">Certificação</option>
                          <option value="idioma">Idioma</option>
                          <option value="outros">Outros</option>
                        </select>
                        
                        <input
                          type="text"
                          value={requisito.descricao}
                          onChange={(e) => updateRequisito(index, 'descricao', e.target.value)}
                          placeholder="Descreva o requisito..."
                          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        
                        <div className="flex items-center space-x-2">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={requisito.obrigatorio}
                              onChange={(e) => updateRequisito(index, 'obrigatorio', e.target.checked)}
                              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                            />
                            <span className="text-sm font-medium text-red-700">Obrigatório</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => removeRequisito(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                      
                      <input
                        type="text"
                        value={requisito.detalhes || ''}
                        onChange={(e) => updateRequisito(index, 'detalhes', e.target.value)}
                        placeholder="Detalhes adicionais (opcional)..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Condições de Trabalho */}
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-4">
                  <div className="rounded-2xl bg-slate-100 p-3">
                    <Building className="h-5 w-5 text-slate-700" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">Operação</p>
                    <h3 className="text-lg font-semibold text-slate-900">Condições de trabalho</h3>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ambiente de Trabalho
                    </label>
                    <select
                      value={formData.condicoes_trabalho.ambiente}
                      onChange={(e) => setFormData({
                        ...formData,
                        condicoes_trabalho: {
                          ...formData.condicoes_trabalho,
                          ambiente: e.target.value
                        }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="escritorio">Escritório</option>
                      <option value="campo">Campo</option>
                      <option value="fabrica">Fábrica</option>
                      <option value="hibrido">Híbrido</option>
                      <option value="remoto">Remoto</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                      Horário de Trabalho
                      <span className="text-xs text-gray-500">(do Sistema de Ponto)</span>
                    </label>
                    <select
                      value={formData.condicoes_trabalho.horario}
                      onChange={(e) => setFormData({
                        ...formData,
                        condicoes_trabalho: {
                          ...formData.condicoes_trabalho,
                          horario: e.target.value
                        }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione um horário</option>
                      {horariosTrabalho.length > 0 ? (
                        horariosTrabalho.map(horario => (
                          <option key={horario.id} value={horario.id}>
                            {horario.nome} - {horario.entrada} às {horario.saida} 
                            {horario.intervalo_inicio && ` (intervalo: ${horario.intervalo_inicio} - ${horario.intervalo_fim})`}
                            {' '}({horario.carga_horaria_diaria}h/dia)
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>Nenhum horário cadastrado</option>
                      )}
                    </select>
                    {horariosTrabalho.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ Cadastre horários em: Administração → Sistema de Ponto
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.condicoes_trabalho.viagens}
                        onChange={(e) => setFormData({
                          ...formData,
                          condicoes_trabalho: {
                            ...formData.condicoes_trabalho,
                            viagens: e.target.checked
                          }
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Viagens</span>
                    </label>
                    
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.condicoes_trabalho.home_office}
                        onChange={(e) => setFormData({
                          ...formData,
                          condicoes_trabalho: {
                            ...formData.condicoes_trabalho,
                            home_office: e.target.checked
                          }
                        })}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Home Office</span>
                    </label>
                  </div>
                </div>
              </div>

                </div>
              </div>

              <div className="border-t border-slate-200 bg-white/95 px-6 py-4 backdrop-blur">
                <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="rounded-full border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                
                <button
                  type="submit"
                  className="rounded-full bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 transition-colors"
                >
                  {isEditMode ? '💾 Salvar Alterações' : '➕ Criar DCF'}
                </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Modal de Visualização */}
      {showViewModal && cargoSelecionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[28px] border border-slate-200 shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-blue-900 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Visualizar Cargo</h2>
                <p className="text-blue-100 text-sm mt-1">Resumo executivo da DCF cadastrada</p>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-lg"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Cargo</label>
                    <p className="text-gray-900 font-semibold">{cargoSelecionado.nome_cargo}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                    <p className="text-gray-900">{cargoSelecionado.codigo_cargo}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Departamento</label>
                    <p className="text-gray-900">{cargoSelecionado.departamento}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nível Hierárquico</label>
                    <p className="text-gray-900 capitalize">{cargoSelecionado.nivel_hierarquico}</p>
                  </div>
                </div>
                
                {cargoSelecionado.objetivo_cargo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Objetivo do Cargo</label>
                    <p className="text-gray-900">{cargoSelecionado.objetivo_cargo}</p>
                  </div>
                )}
                
                {cargoSelecionado.atividades && cargoSelecionado.atividades.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Atividades ({cargoSelecionado.atividades.length})</label>
                    <div className="space-y-2">
                      {cargoSelecionado.atividades.map((atividade, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-gray-900">{atividade.descricao}</p>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                            <span className="capitalize">Tipo: {atividade.tipo}</span>
                            <span>Tempo: {atividade.percentual_tempo}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default GerenciarCargos;
